import React, { useEffect, useRef, useState } from 'react';
import { authApi, type AdminAuthUser } from '../../services/api/auth.api';

export const AdminProfilePage: React.FC = () => {
  const [user, setUser] = useState<AdminAuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    displayName: '',
    login: '',
    email: '',
    logoUrl: '',
  });
  const newPasswordRef = useRef<HTMLInputElement>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    authApi
      .me()
      .then((u) => {
        setUser(u);
        setForm({
          displayName: u.displayName || '',
          login: u.login || '',
          email: u.email || '',
          logoUrl: u.logoUrl || '',
        });
      })
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setSaving(true);
    setSuccess(false);
    const passwordRaw = newPasswordRef.current?.value?.trim();
    const password = passwordRaw ? passwordRaw : undefined;
    try {
      const updated = await authApi.updateProfile({
        displayName: form.displayName,
        login: form.login,
        email: form.email,
        logoUrl: form.logoUrl || null,
        password,
      });
      setUser(updated);
      if (newPasswordRef.current) newPasswordRef.current.value = '';
      setSuccess(true);
      window.dispatchEvent(new CustomEvent('admin-profile-updated', { detail: updated }));
    } catch (error) {
      console.error('Failed to update profile:', error);
      alert((error as any)?.response?.data?.error?.message || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-full flex-col font-sans text-slate-900">
        <main className="flex-grow w-full px-4 py-8 sm:px-6 lg:px-8">
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600"></div>
          </div>
        </main>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-full flex-col font-sans text-slate-900">
        <main className="flex-grow w-full px-4 py-8 sm:px-6 lg:px-8">
          <div className="mb-8 w-full rounded-[2rem] border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-slate-50 p-6 shadow-sm">
            <p className="text-center text-slate-500">Impossible de charger votre profil.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-col font-sans text-slate-900">
      <main className="flex-grow w-full px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 w-full max-w-2xl rounded-[2rem] border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-slate-50 p-6 shadow-sm">
          <div className="mb-6">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-500">Mon compte</div>
            <h1 className="mt-2 text-2xl font-black tracking-tight text-slate-900">Profil</h1>
            <p className="mt-2 text-slate-500">Modifiez vos informations personnelles.</p>
          </div>

          {user.isImpersonating && (
            <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <p className="font-bold">Vous êtes en impersonation</p>
              <p className="mt-1 text-amber-800/90">
                Les changements (y compris le mot de passe) s’appliquent au compte{' '}
                <span className="font-semibold">{user.displayName}</span>, pas à votre compte super admin. Pour
                modifier votre propre mot de passe, revenez d’abord au super admin via la bannière en haut de page.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
          {success && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800">
              Profil mis à jour avec succès.
            </div>
          )}

          <div>
            <label className="mb-2 ml-1 block text-xs font-bold uppercase tracking-wider text-slate-400">
              Nom affiché
            </label>
            <input
              type="text"
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              className="w-full rounded-2xl border-2 border-slate-100 bg-slate-50 px-5 py-4 text-lg font-bold text-slate-900 transition-all focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-500/10"
              required
            />
          </div>

          <div>
            <label className="mb-2 ml-1 block text-xs font-bold uppercase tracking-wider text-slate-400">
              URL du logo
            </label>
            <input
              type="text"
              value={form.logoUrl}
              onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
              placeholder="https://... ou /logos/mon-logo.png"
              className="w-full rounded-2xl border-2 border-slate-100 bg-slate-50 px-5 py-4 text-lg font-bold text-slate-900 placeholder-slate-300 transition-all focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-500/10"
            />
            {form.logoUrl && (
              <div className="mt-2 flex items-center gap-3">
                <img
                  src={form.logoUrl}
                  alt="Aperçu"
                  className="h-12 w-12 rounded-full object-contain bg-white border border-slate-200"
                  onError={(e) => (e.currentTarget.style.display = 'none')}
                />
                <span className="text-xs text-slate-500">Aperçu</span>
              </div>
            )}
          </div>

          <div>
            <label className="mb-2 ml-1 block text-xs font-bold uppercase tracking-wider text-slate-400">Login</label>
            <input
              type="text"
              value={form.login}
              onChange={(e) => setForm({ ...form, login: e.target.value })}
              className="w-full rounded-2xl border-2 border-slate-100 bg-slate-50 px-5 py-4 text-lg font-bold text-slate-900 transition-all focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-500/10"
              required
            />
          </div>

          <div>
            <label className="mb-2 ml-1 block text-xs font-bold uppercase tracking-wider text-slate-400">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full rounded-2xl border-2 border-slate-100 bg-slate-50 px-5 py-4 text-lg font-bold text-slate-900 transition-all focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-500/10"
              required
            />
          </div>

          <div>
            <label className="mb-2 ml-1 block text-xs font-bold uppercase tracking-wider text-slate-400">
              Nouveau mot de passe
            </label>
            <input
              key={`profile-new-password-${user.id}`}
              ref={newPasswordRef}
              type="password"
              name="new-password"
              autoComplete="new-password"
              defaultValue=""
              placeholder="Laisser vide pour ne pas changer"
              className="w-full rounded-2xl border-2 border-slate-100 bg-slate-50 px-5 py-4 text-lg font-bold text-slate-900 placeholder-slate-300 transition-all focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-500/10"
            />
          </div>

          <div className="flex gap-4 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-slate-900 px-8 py-4 text-lg font-black text-white shadow-xl shadow-slate-200 transition-all hover:scale-[1.01] hover:bg-indigo-600 hover:shadow-indigo-200 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? 'Enregistrement...' : 'Enregistrer'}
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
              </svg>
            </button>
          </div>
        </form>
        </div>
      </main>
    </div>
  );
};
